// Comprobantes de Farmacia Interna (Fase 3-4, 2026-09-11) — anular / nota de
// crédito / nota de débito / descargar RIDE, mismo motor que
// /admin/comprobantes del CRM (lib/notas-credito.ts), acotado a las facturas
// propias de farmacia. Norma 2026: una factura a consumidor final transmitida
// desde 2026-01-01 queda bloqueada para anular/NC (bloqueadaPorNormaCF).
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  anularComprobanteFarmacia,
  descargarComprobanteFarmacia,
  emitirNotaCreditoFarmacia,
  emitirNotaDebitoFarmacia,
  listarComprobantesFarmacia,
  type FacturaFarmacia,
} from '@/lib/farmaciaInterna';
import { DevolverVentaFarmacia } from './DevolverVentaFarmacia';
import { EditorLineasNotaFarmacia } from './EditorLineasNotaFarmacia';
import { calcularNota, filasIniciales, type FilaNota } from '@/lib/notaLineas';

function formatoUsd(cents: number): string {
  return (cents / 100).toLocaleString('es-EC', { style: 'currency', currency: 'USD' });
}

/** Exportado para OrdenesFarmacia.tsx (panel de resultado de venta, PR 8.3): un solo lugar que arma el blob y dispara la descarga. */
export function descargar(filename: string, contentType: string, content: string): void {
  const esHtml = contentType.includes('html');
  const blob = esHtml ? new Blob([content], { type: contentType }) : new Blob([Uint8Array.from(atob(content), (c) => c.charCodeAt(0))], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** La nota de débito no se ofrece hasta reescribirla contra su XSD (Plan UX cobro, banderas §12.6); el backend queda. */
const NOTA_DEBITO_VISIBLE = false;

type AccionAbierta = { invoiceId: number; tipo: 'anular' | 'nota-credito' | 'nota-debito' };

export function ComprobantesFarmacia(): JSX.Element {
  const { t } = useTranslation();
  const [facturas, setFacturas] = useState<FacturaFarmacia[]>();
  const [disponible, setDisponible] = useState(true);
  const [accion, setAccion] = useState<AccionAbierta>();
  const [motivo, setMotivo] = useState('');
  const [monto, setMonto] = useState('');
  // Factura de tarifas mezcladas: editor «Detalle por líneas» en vez del monto suelto (que no sabe a qué tarifa aplicarse).
  const [filas, setFilas] = useState<FilaNota[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string>();
  const [aviso, setAviso] = useState<string>();

  async function recargar(): Promise<void> {
    const r = await listarComprobantesFarmacia();
    setFacturas(r.facturas);
    setDisponible(r.disponible);
  }

  useEffect(() => {
    void recargar();
  }, []);

  function abrir(invoiceId: number, tipo: AccionAbierta['tipo']): void {
    setAccion({ invoiceId, tipo });
    setMotivo('');
    setMonto('');
    const lineas = facturas?.find((f) => f.id === invoiceId)?.lineas;
    setFilas(lineas && tipo !== 'anular' ? filasIniciales(lineas, tipo === 'nota-credito' ? 'nc' : 'nd') : []);
    setError(undefined);
  }

  async function confirmar(): Promise<void> {
    if (!accion) return;
    const motivoLimpio = motivo.trim();
    if (!motivoLimpio) {
      setError(t('farmaciaApp.comprobantes.motivoRequerido'));
      return;
    }
    setEnviando(true);
    setError(undefined);
    try {
      if (accion.tipo === 'anular') {
        const r = await anularComprobanteFarmacia(accion.invoiceId, motivoLimpio);
        if (!r.ok) {
          setError(r.error);
          return;
        }
        setAviso(t('farmaciaApp.comprobantes.anulada'));
      } else if (accion.tipo === 'nota-credito') {
        const factura = facturas?.find((f) => f.id === accion.invoiceId);
        const detalle = factura?.lineas ? calcularNota(filas, 'nc', factura.saldoCents) : null;
        if (detalle?.error) {
          setError(detalle.error);
          return;
        }
        const montoCents = detalle ? detalle.totalCents : monto.trim() ? Math.round(Number(monto.replace(',', '.')) * 100) : undefined;
        const r = await emitirNotaCreditoFarmacia(accion.invoiceId, { motivo: motivoLimpio, montoCents, ...(detalle ? { lines: detalle.lines } : {}) });
        if (!r.ok) {
          setError(r.error);
          return;
        }
        setAviso(t('farmaciaApp.comprobantes.notaCreditoEmitida', { monto: (r.datos.totalCents / 100).toFixed(2) }));
      } else {
        const factura = facturas?.find((f) => f.id === accion.invoiceId);
        const detalle = factura?.lineas ? calcularNota(filas, 'nd', null) : null;
        if (detalle?.error) {
          setError(detalle.error);
          return;
        }
        const montoCents = detalle ? detalle.totalCents : Math.round(Number(monto.replace(',', '.')) * 100);
        if (!Number.isInteger(montoCents) || montoCents <= 0) {
          setError(t('farmaciaApp.comprobantes.montoInvalido'));
          return;
        }
        const r = await emitirNotaDebitoFarmacia(accion.invoiceId, { motivo: motivoLimpio, montoCents, ...(detalle ? { lines: detalle.lines } : {}) });
        if (!r.ok) {
          setError(r.error);
          return;
        }
        setAviso(t('farmaciaApp.comprobantes.notaDebitoEmitida', { monto: (r.datos.totalCents / 100).toFixed(2) }));
      }
      setAccion(undefined);
      await recargar();
    } finally {
      setEnviando(false);
    }
  }

  async function bajarComprobante(invoiceId: number, tipo: 'ride' | 'xml'): Promise<void> {
    setError(undefined);
    const r = await descargarComprobanteFarmacia(invoiceId, tipo);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    descargar(r.datos.filename, r.datos.contentType, r.datos.content);
  }

  if (facturas === undefined) {
    return <p className="text-sm text-hce-muted">{t('farmaciaApp.cargando')}</p>;
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {aviso && (
        <Alert>
          <AlertDescription>{aviso}</AlertDescription>
        </Alert>
      )}

      {!disponible ? (
        <p className="text-sm text-hce-muted">{t('farmaciaApp.noDisponible')}</p>
      ) : facturas.length === 0 ? (
        <p className="text-sm text-hce-muted">{t('farmaciaApp.comprobantes.vacio')}</p>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {facturas.map((f) => (
            <div key={f.id} className="space-y-2 px-3 py-2.5 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-hce-ink">
                    #{f.id} · {f.buyerName} · {formatoUsd(f.totalCents)}
                  </span>
                  <span className="block text-xs text-hce-muted">
                    {t('farmaciaApp.comprobantes.saldo', { monto: (f.saldoCents / 100).toFixed(2) })}
                    {f.bloqueadaPorNormaCF && ` · ${t('farmaciaApp.comprobantes.bloqueadaNorma')}`}
                  </span>
                </span>
                <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                  <Button size="sm" variant="ghost" onClick={() => void bajarComprobante(f.id, 'ride')}>
                    {t('farmaciaApp.comprobantes.descargarRide')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void bajarComprobante(f.id, 'xml')}>
                    {t('farmaciaApp.comprobantes.descargarXml')}
                  </Button>
                  <DevolverVentaFarmacia factura={f} onCambio={recargar} />
                  {!f.bloqueadaPorNormaCF && (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => abrir(f.id, 'nota-credito')}>
                        {t('farmaciaApp.comprobantes.notaCredito')}
                      </Button>
                      {NOTA_DEBITO_VISIBLE && (
                        <Button size="sm" variant="secondary" onClick={() => abrir(f.id, 'nota-debito')}>
                          {t('farmaciaApp.comprobantes.notaDebito')}
                        </Button>
                      )}
                      <Button size="sm" variant="destructive" onClick={() => abrir(f.id, 'anular')}>
                        {t('farmaciaApp.comprobantes.anular')}
                      </Button>
                    </>
                  )}
                </span>
              </div>

              {accion?.invoiceId === f.id && (
                <div className="flex flex-wrap items-end gap-2 rounded-md bg-hce-paper p-2.5">
                  {accion.tipo !== 'anular' && f.lineas && (
                    <EditorLineasNotaFarmacia tipo={accion.tipo === 'nota-credito' ? 'nc' : 'nd'} filas={filas} resultado={calcularNota(filas, accion.tipo === 'nota-credito' ? 'nc' : 'nd', accion.tipo === 'nota-credito' ? f.saldoCents : null)} onChange={setFilas} />
                  )}
                  {accion.tipo !== 'anular' && !f.lineas && (
                    <div>
                      <label className="mb-1 block text-xs text-hce-muted">
                        {accion.tipo === 'nota-debito' ? t('farmaciaApp.comprobantes.montoObligatorio') : t('farmaciaApp.comprobantes.montoOpcional')}
                      </label>
                      <Input type="number" step="0.01" min="0" className="h-9 w-32" value={monto} onChange={(e) => setMonto(e.target.value)} />
                    </div>
                  )}
                  <div className="min-w-48 flex-1">
                    <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.comprobantes.motivo')}</label>
                    <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                  </div>
                  <Button size="sm" disabled={enviando} onClick={() => void confirmar()}>
                    {enviando ? t('farmaciaApp.comprobantes.enviando') : t('farmaciaApp.comprobantes.confirmar')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setAccion(undefined)}>
                    {t('farmaciaApp.catalogo.cancelar')}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
